import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { User } from "src/api/types";
import GlobalContext from "src/context/global-context";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage, logger } from "src/lib/utils";
import client from "../../api";
import { PDFExport } from "@progress/kendo-react-pdf";
import UserConsentPDF from "./UserConsentPDF";

type Props = {
  user: User;
};

interface ConsentData {
  id: number;
  user_id: number;
  terms_and_conditions: boolean;
  cookie_policy: boolean;
  created_at: string;
  updated_at: string;
}

const UserConsentTab: FC<Props> = ({ user }) => {
  const [dataSource, setDataSource] = useState<ConsentData | null>(null);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const pdfExportComponent = useRef<PDFExport>(null);

  const handleDownload = () => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save();
    }
  };
  /* ---------------- GET Consent details ---------------- */

  const { mutate: getConsentDetails, isLoading } = useMutation(
    client.newUserKycInfo.getInfoConsent,
    {
      onSuccess: (data) => {
        logger.log("Success data", data);
        setDataSource(data?.data as ConsentData);
        if (data?.message === "You do not have access for this route!") {
          setAlertText(data?.message);
          setIsAlertOpened(true);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (user?.id) getConsentDetails({ id: user.id });
  }, [user?.id]);

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-40 text-gray-400">
        Loading...
      </div>
    );

  return (
    <div className="flex flex-col items-center py-6">
      {/* Button Row */}
      <div className="w-full max-w-2xl flex justify-end mb-3">
        <button
          onClick={handleDownload}
          disabled={!dataSource}
          className={`px-4 py-2 rounded-md text-sm font-medium transition
          ${
            dataSource
              ? "bg-[#232800] text-white hover:opacity-90"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Download PDF
        </button>
      </div>

      {/* Consent Card */}
      <div className="w-full max-w-2xl bg-white border rounded-xl shadow-sm p-6 text-sm text-gray-800 space-y-4">
        {dataSource?.id ? (
          <>
            <h2 className="font-semibold tracking-wide text-base border-b pb-2">
              CONSENT CONFIRMATION
            </h2>

            <p>
              This document confirms that the user identified by{" "}
              <strong>User ID {dataSource.user_id}</strong> has provided consent
              to the following policies within the system.
            </p>

            <div>
              <p className="font-medium">Accepted Policies:</p>
              <ul className="list-disc ml-6 mt-1">
                {dataSource.terms_and_conditions && (
                  <li>Terms and Conditions</li>
                )}
                {dataSource.cookie_policy && <li>Cookie Policy</li>}
              </ul>
            </div>

            <div>
              <p className="font-medium">Consent Status:</p>
              <ul className="list-disc ml-6 mt-1">
                <li>
                  Terms and Conditions:{" "}
                  {dataSource.terms_and_conditions
                    ? "Accepted"
                    : "Not Accepted"}
                </li>
                <li>
                  Cookie Policy:{" "}
                  {dataSource.cookie_policy ? "Accepted" : "Not Accepted"}
                </li>
              </ul>
            </div>

            <div>
              <p className="font-medium">Record Details:</p>
              <ul className="list-disc ml-6 mt-1">
                <li>Record ID: {dataSource.id}</li>
                <li>
                  Created At: {new Date(dataSource.created_at).toLocaleString()}
                </li>
                <li>
                  Last Updated:{" "}
                  {new Date(dataSource.updated_at).toLocaleString()}
                </li>
              </ul>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center h-40 text-gray-400">
            No Consent data found
          </div>
        )}
      </div>

      {/* Hidden PDF */}
      <div style={{ position: "fixed", left: "-10000px" }}>
        <PDFExport
          ref={pdfExportComponent}
          paperSize="A4"
          fileName={`consent-user-${dataSource?.user_id}.pdf`}
        >
          {dataSource && <UserConsentPDF data={dataSource} />}
        </PDFExport>
      </div>
    </div>
  );
};

export default UserConsentTab;
